import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth, ROSTER_API } from '../utils/api';
import { 
  Participant, 
  RegistrationData, 
  PricingSettings, 
  CriteriaSettings 
} from '../types/registration';
import { 
  WorkshopLevel, 
  AgeDivision, 
  UseDancersDataReturn 
} from '../types/dancers';

// Define types for API responses
interface RosterMember {
  dancerId: string;
  firstName: string;
  lastName: string;
  role: string[] | { S: string }[] | string;
  dateOfBirth?: string;
  birthDate?: string;
  email?: string;
  id?: string;
  participantType?: 'dancer' | 'teacher' | 'observer';
  profilePicture?: string;
  [key: string]: any;
}

interface ApiResponse {
  roster: RosterMember[];
  [key: string]: any;
}

// Convert RosterMember to Participant
const rosterMemberToParticipant = (member: RosterMember): Participant => {
  return {
    id: member.id || member.dancerId,
    dancerId: member.dancerId,
    firstName: member.firstName,
    lastName: member.lastName,
    dateOfBirth: member.dateOfBirth || member.birthDate,
    email: member.email,
    participantType: member.participantType || 'dancer',
    profilePicture: member.profilePicture || null,
    // Other required fields with defaults
    options: {},
    pendingCreation: false, // Flag to indicate dancer exists only in local state, not in DynamoDB yet
  };
};

/**
 * Custom hook to manage dancers data, state, and operations
 * for the dancer selection step in the registration flow
 */
const useDancersData = (
  registrationData: RegistrationData,
  updateRegistrationData: (data: any, isComplete?: boolean) => Promise<any>,
  pricingSettings: PricingSettings | null,
  criteriaSettings: CriteriaSettings | null
): UseDancersDataReturn => {
  // === STATE MANAGEMENT ===
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dancers, setDancers] = useState<Participant[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedDancers, setSelectedDancers] = useState<Participant[]>([]);
  const [selectedSearchQuery, setSelectedSearchQuery] = useState<string>("");
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [warningDancer, setWarningDancer] = useState<Participant | null>(null);
  const [userSelectedLevels, setUserSelectedLevels] = useState<Record<string, boolean>>({});
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState<boolean>(false);
  const [dancerToRemove, setDancerToRemove] = useState<Participant | null>(null);

  // === COMPUTED PROPERTIES ===

  // Workshop levels normalized from various possible sources
  const workshopLevels = useMemo<WorkshopLevel[]>(() => {
    const registrationPricing =
      pricingSettings?.registrationPricing ||
      pricingSettings?.pricing?.registrationPricing ||
      pricingSettings?.data?.registrationPricing ||
      pricingSettings?.levels ||
      [];

    console.log("Available pricing settings:", pricingSettings);
    console.log("Registration pricing data:", registrationPricing);

    const normalizedLevels = (registrationPricing || []).map((level: any) => ({
      id: level.id || level.name || level.label,
      label: level.ageGroup || level.name || level.label,
      price: parseFloat(level.discountRate || level.price || level.cost || 0),
      minAge: level.minAge !== null ? parseInt(level.minAge) : null,
      maxAge: level.maxAge !== null ? parseInt(level.maxAge) : null,
    }));

    console.log("Normalized workshop levels:", normalizedLevels);
    return normalizedLevels;
  }, [pricingSettings]);

  // Age divisions normalized from criteria settings
  const ageDivisions = useMemo<AgeDivision[]>(() => {
    const ageGroups =
      criteriaSettings?.ageGroups ||
      criteriaSettings?.ages ||
      criteriaSettings?.data?.ageGroups ||
      criteriaSettings?.criteria?.ageGroups ||
      [];

    console.log("Available criteria settings:", criteriaSettings);
    console.log("Age groups data:", ageGroups);

    const normalizedDivisions = (ageGroups || []).map((group: any) => ({
      label: group.label || group.name,
      min:
        group.minAge !== null
          ? parseInt(group.minAge)
          : group.hasNoMinAge
          ? 0
          : null,
      max:
        group.maxAge !== null
          ? parseInt(group.maxAge)
          : group.hasNoMaxAge
          ? 999
          : null,
    }));

    console.log("Normalized age divisions:", normalizedDivisions);
    return normalizedDivisions;
  }, [criteriaSettings]);

  // Filter dancers based on search query
  const filteredDancers = useMemo<Participant[]>(() => {
    return dancers.filter((dancer) => {
      const fullName = `${dancer.firstName} ${dancer.lastName}`.toLowerCase();
      return fullName.includes(searchQuery.toLowerCase());
    });
  }, [dancers, searchQuery]);

  // Filter selected dancers based on search query inside Selected Dancers card
  const filteredSelectedDancers = useMemo<Participant[]>(() => {
    if (!selectedSearchQuery) return selectedDancers;
    const query = selectedSearchQuery.toLowerCase();
    return selectedDancers.filter((dancer) => {
      const fullName = `${dancer.firstName} ${dancer.lastName}`.toLowerCase();
      return fullName.includes(query);
    });
  }, [selectedDancers, selectedSearchQuery]);

  // Helper function to consistently get workshop level
  const getWorkshopLevel = useCallback((dancer: Participant): string => {
    // Add some debug logging to help diagnose the issue
    console.log("Getting workshop level for dancer:", dancer.firstName, dancer.lastName);
    console.log("workshopLevel:", dancer.workshopLevel);
    console.log("options:", dancer.options);
    
    // First check the direct workshopLevel property
    if (dancer.workshopLevel) {
      return dancer.workshopLevel;
    }
    
    // Handle different options formats
    if (dancer.options) {
      // Direct workshopLevel in options
      if (dancer.options.workshopLevel && typeof dancer.options.workshopLevel === 'string') {
        return dancer.options.workshopLevel;
      }
      
      // DynamoDB format: options.workshopLevel.S
      if (dancer.options.workshopLevel && 
          typeof dancer.options.workshopLevel === 'object' && 
          'S' in (dancer.options.workshopLevel as Record<string, any>)) {
        return (dancer.options.workshopLevel as { S: string }).S;
      }
      
      // Handle stringified JSON (happens sometimes with DynamoDB)
      if (typeof dancer.options === 'string') {
        try {
          const parsedOptions = JSON.parse(dancer.options);
          if (parsedOptions.workshopLevel) {
            if (typeof parsedOptions.workshopLevel === 'string') {
              return parsedOptions.workshopLevel;
            }
            if (typeof parsedOptions.workshopLevel === 'object' && 
                'S' in parsedOptions.workshopLevel) {
              return parsedOptions.workshopLevel.S;
            }
          }
        } catch (e) {
          console.error("Error parsing options string:", e);
        }
      }
    }
    
    return "";
  }, []);

  // Total price calculation
  const totalPrice = useMemo<number>(() => {
    return selectedDancers.reduce((total, dancer) => {
      const levelId = getWorkshopLevel(dancer);
      if (!levelId) return total;

      const level = workshopLevels.find(
        (lvl) => lvl.id === levelId || lvl.label === levelId
      );
      return total + (level?.price || 0);
    }, 0);
  }, [selectedDancers, workshopLevels, getWorkshopLevel]);

  // === UTILITY FUNCTIONS ===

  // Calculate age from birth date
  const calculateAge = useCallback((birthDate: string): number | string => {
    if (!birthDate) return "N/A";

    try {
      const birth = new Date(birthDate);
      const today = new Date();

      if (isNaN(birth.getTime())) return "N/A";

      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }

      return age;
    } catch (error) {
      return "N/A";
    }
  }, []);

  // Get dancer initials
  const getInitials = useCallback((firstName: string, lastName: string): string => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  }, []);

  // Normalize roles for consistent processing
  const normalizeRoles = useCallback((roleValue: any): string[] => {
    if (!roleValue) return [];
    if (Array.isArray(roleValue) && typeof roleValue[0] === "string") {
      return roleValue;
    }
    if (
      Array.isArray(roleValue) &&
      typeof roleValue[0] === "object" &&
      "S" in roleValue[0]
    ) {
      return roleValue.map((r) => r.S);
    }
    if (typeof roleValue === "string") {
      return [roleValue];
    }
    return [];
  }, []);

  // Find age group info
  const findAgeGroupInfo = useCallback(
    (levelLabel: string): any => {
      if (
        !criteriaSettings?.ageGroups ||
        !Array.isArray(criteriaSettings.ageGroups)
      ) {
        return null;
      }
      return criteriaSettings.ageGroups.find((ag) => ag.label === levelLabel);
    },
    [criteriaSettings]
  );

  // Format age range for display
  const formatAgeRange = useCallback((ageGroupInfo: any): string => {
    if (!ageGroupInfo) return "";

    const minAge =
      ageGroupInfo.minAge !== null && ageGroupInfo.minAge !== undefined
        ? parseInt(ageGroupInfo.minAge)
        : ageGroupInfo.hasNoMinAge === true
        ? 0
        : null;

    const maxAge =
      ageGroupInfo.maxAge !== null && ageGroupInfo.maxAge !== undefined
        ? parseInt(ageGroupInfo.maxAge)
        : ageGroupInfo.hasNoMaxAge === true
        ? 999
        : null;

    if (minAge === null && maxAge === null) return "(No age specified)";
    if (minAge === 0 && maxAge === 999) return "(All Ages)";
    if (minAge !== null && maxAge !== null) {
      if (minAge === maxAge) return `(Age ${minAge})`;
      return `(Ages ${minAge}-${maxAge})`;
    }
    if (minAge !== null) return `(Ages ${minAge}+)`;
    if (maxAge !== null) return `(Ages up to ${maxAge})`;
    return "";
  }, []);

  // Get suggested workshop level based on age
  const getSuggestedWorkshopLevel = useCallback(
    (age: number | string): string | null => {
      // If age is invalid, return null
      if (
        !age ||
        age === "N/A" ||
        !Array.isArray(ageDivisions) ||
        ageDivisions.length === 0 ||
        !Array.isArray(workshopLevels) ||
        workshopLevels.length === 0
      ) {
        console.log("Cannot determine suggested level: Invalid age or no age divisions/workshop levels");
        return null;
      }

      // Convert age to numeric if it's a string
      const numericAge = typeof age === 'string' ? parseInt(age, 10) : age;
      
      // Check if age is a valid number
      if (isNaN(numericAge)) {
        console.log("Cannot determine suggested level: Age is not a number", age);
        return null;
      }

      console.log(`Finding suggested level for age: ${numericAge}`);
      console.log("Available age divisions:", ageDivisions);
      console.log("Available workshop levels:", workshopLevels.map(l => l.label));

      // Find matching age division - logging details for debugging
      const matchingDivisions = ageDivisions
        .filter((division) => {
          const minMatch = division.min === null || numericAge >= division.min;
          const maxMatch = division.max === null || numericAge <= division.max;
          const exactMatch = division.min === division.max && division.min === numericAge;
          
          const matches = (minMatch && maxMatch) || exactMatch;
          console.log(`Division: ${division.label}, Min: ${division.min}, Max: ${division.max}, Matches: ${matches}`);
          
          return matches;
        })
        .sort((a, b) => {
          // Sort by age range specificity (narrower ranges first)
          const aRange = (a.max || 999) - (a.min || 0);
          const bRange = (b.max || 999) - (b.min || 0);
          return aRange - bRange;
        });

      if (matchingDivisions.length === 0) {
        console.log("No matching age division found");
        return null;
      }

      // Try each matching division until we find one that's in the workshop levels
      for (const division of matchingDivisions) {
        const divisionLabel = division.label;
        console.log(`Checking division: ${divisionLabel}`);

        // Check if this level exists in the available workshop levels
      const levelExists = workshopLevels.some(
          (level) => level.label === divisionLabel || level.id === divisionLabel
        );

        if (levelExists) {
          console.log(`Found valid workshop level: ${divisionLabel}`);
          return divisionLabel;
        } else {
          console.log(`Division "${divisionLabel}" not found in available workshop levels, trying next...`);
        }
      }

      // If no exact match, try to find a workshop level with a similar age range
      console.log("No exact division match in workshop levels, looking for alternative...");
      
      // Find a workshop level that covers this age
      for (const level of workshopLevels) {
        if (level.minAge !== null && level.maxAge !== null) {
          if (numericAge >= level.minAge && numericAge <= level.maxAge) {
            console.log(`Found workshop level covering this age: ${level.label} (${level.minAge}-${level.maxAge})`);
            return level.label;
          }
        }
      }

      // For young dancers, find the level with the lowest minimum age
      if (numericAge <= 10) {
        const lowestAgeLevels = [...workshopLevels]
          .filter(level => level.minAge !== null)
          .sort((a, b) => (a.minAge || 0) - (b.minAge || 0));
        
        if (lowestAgeLevels.length > 0) {
          const bestLevelForYoungDancer = lowestAgeLevels[0].label;
          console.log(`Found best level for young dancer (${numericAge}): ${bestLevelForYoungDancer}`);
          return bestLevelForYoungDancer;
        }
      }
      
      // Last resort: suggest the first workshop level
      if (workshopLevels.length > 0) {
        const firstLevel = workshopLevels[0].label;
        console.log(`No age-appropriate level found, suggesting first available level: ${firstLevel}`);
        return firstLevel;
      }

      console.log("No suitable workshop level found");
      return null;
    },
    [ageDivisions, workshopLevels]
  );

  // Check if a dancer is already selected
  const isDancerSelected = useCallback(
    (dancerId: string): boolean => {
      return (
        Array.isArray(selectedDancers) &&
        selectedDancers.some(
          (dancer) =>
            dancer.dancerId === dancerId ||
            dancer.originalDancerId === dancerId ||
            dancer.originalId === dancerId ||
            dancer.participantId === dancerId
        )
      );
    },
    [selectedDancers]
  );

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Helper function to debug workshop level suggestions - moved here after utility functions are defined
  const debugWorkshopSuggestion = useCallback((dancer: Participant, source: string) => {
    const age = calculateAge(dancer.dateOfBirth || dancer.birthDate || '');
    const currentLevel = getWorkshopLevel(dancer);
    const suggestedLevel = getSuggestedWorkshopLevel(age);
    
    console.log(`[${source}] Dancer: ${dancer.firstName} ${dancer.lastName}, Age: ${age}, Current Level: ${currentLevel || 'None'}, Suggested: ${suggestedLevel || 'None'}`);
    
    return suggestedLevel;
  }, [calculateAge, getWorkshopLevel, getSuggestedWorkshopLevel]);

  // === EVENT HANDLERS ===

  // Toggle dancer selection
  const toggleDancerSelection = useCallback(
    (dancer: Participant) => {
      // Helper function to find dancer across different ID fields
      const findDancerInSelected = (dancer: Participant) => {
        return selectedDancers.find(
        (d) =>
            // Check all possible ID fields
            (d.dancerId && (d.dancerId === dancer.dancerId || d.dancerId === dancer.originalId)) ||
            (d.originalId && (d.originalId === dancer.dancerId || d.originalId === dancer.originalId)) ||
            (d.originalDancerId && (d.originalDancerId === dancer.dancerId || d.originalDancerId === dancer.originalId)) ||
            (d.participantId && d.participantId === dancer.participantId) ||
            // As a fallback, check if names and birth dates match
            (d.firstName === dancer.firstName && 
             d.lastName === dancer.lastName && 
             (d.dateOfBirth === dancer.dateOfBirth || d.birthDate === dancer.birthDate))
        );
      };

      // Check if dancer is already selected using our improved finder
      const selectedDancer = findDancerInSelected(dancer);
      const isSelected = !!selectedDancer;

      if (isSelected) {
        // Check if dancer is from a routine
        const isFromRoutine =
          selectedDancer.isFromRoutine ||
          selectedDancer.fromRoutine ||
          selectedDancer.autoAdded ||
          selectedDancer.cannotBeRemoved ||
          selectedDancer.routineSource;

        if (isFromRoutine) {
          setWarningDancer(selectedDancer);
          setShowWarning(true);
          return; // Return void here - no promise
        }

        // For removing a dancer, instead of immediate removal, show confirmation dialog
        setDancerToRemove(selectedDancer);
        setShowRemoveConfirmation(true);
        return; // Return void here - no promise
        } else {
        // For adding a dancer - THIS IS WHERE WE DO NOT CREATE DYNAMODB RECORD YET
        // Get a suggested workshop level for UI display only
        const suggestedLevel = debugWorkshopSuggestion(dancer, 'adding');
        console.log(`Adding dancer ${dancer.firstName} ${dancer.lastName} with suggested level: ${suggestedLevel}`);
        
        // Create a new dancer object for local state only (no DynamoDB record yet)
          const newDancer = {
            ...dancer,
            originalId: dancer.dancerId, // Store original ID to help with deduplication
            workshopLevel: null, // Don't auto-select the level
            options: {
              fullConvention: true,
              competition: false,
              workshopLevel: null, // Don't auto-select the level
            },
            // Flag to indicate this dancer exists only in local state
            pendingCreation: true 
          };

        // Update local state only
          const updatedDancers = [...selectedDancers, newDancer];
          setSelectedDancers(updatedDancers);
        
        // Update registration data in local state 
          updateRegistrationData({
            registrationDancers: updatedDancers,
            dancers: updatedDancers,
            dancerIds: updatedDancers.map((d) => d.dancerId || d.originalId),
          });
        
        return; // Return void - no promise
      }
    },
    [
      selectedDancers,
      registrationData,
      getSuggestedWorkshopLevel,
      calculateAge,
      updateRegistrationData,
      setUserSelectedLevels,
      setError,
      setWarningDancer,
      setShowWarning,
      debugWorkshopSuggestion,
      setDancerToRemove,
      setShowRemoveConfirmation
    ]
  );

  // Function to confirm dancer removal
  const confirmDancerRemoval = useCallback(() => {
    if (!dancerToRemove) return;
    
    // Store dancer in a local variable before clearing the state
    const dancerToProcess = dancerToRemove;
    
    // Clear confirmation dialog state
    setShowRemoveConfirmation(false);
    setDancerToRemove(null);
    
    // Log the dancer being removed for debugging purposes
    console.log("Attempting to remove dancer:", dancerToProcess);
    
    // Create a robust function for identifying the specific dancer
    const isDancerToRemove = (d: Participant): boolean => {
      // If we have participantId, prioritize that
      if (dancerToProcess.participantId && d.participantId) {
        return d.participantId === dancerToProcess.participantId;
      }
      
      // Next check dancerId
      if (dancerToProcess.dancerId && d.dancerId) {
        return d.dancerId === dancerToProcess.dancerId;
      }
      
      // Check originalId
      if (dancerToProcess.originalId && d.originalId) {
        return d.originalId === dancerToProcess.originalId;
      }
      
      // Check originalDancerId
      if (dancerToProcess.originalDancerId && d.originalDancerId) {
        return d.originalDancerId === dancerToProcess.originalDancerId;
      }
      
      // As a last resort, check for name and DOB match
      if (dancerToProcess.firstName && 
          dancerToProcess.lastName && 
          (dancerToProcess.dateOfBirth || dancerToProcess.birthDate)) {
        return (
          d.firstName === dancerToProcess.firstName &&
          d.lastName === dancerToProcess.lastName &&
          (d.dateOfBirth === dancerToProcess.dateOfBirth || 
           d.birthDate === dancerToProcess.birthDate)
        );
      }
      
      // No sufficient matching criteria
      return false;
    };

    // Update local state only - no API calls for removal
    setSelectedDancers((prevDancers) => {
      console.log("Previous dancers count:", prevDancers.length);
      
      // Filter out the specific dancer we want to remove using our robust function
      const updatedDancers = prevDancers.filter(dancer => !isDancerToRemove(dancer));
      
      console.log("Updated dancers count:", updatedDancers.length);
      
      // Also update registration data with the filtered dancers
      updateRegistrationData({
        registrationDancers: updatedDancers,
        dancers: updatedDancers,
        dancerIds: updatedDancers.map((d) => d.dancerId || d.originalId),
      });
      
      return updatedDancers;
    });
    
    // Note: We no longer make API calls here - we'll handle all API interactions in handleNext
  }, [dancerToRemove, updateRegistrationData]);

  // Function to cancel dancer removal
  const cancelDancerRemoval = useCallback(() => {
    setShowRemoveConfirmation(false);
    setDancerToRemove(null);
  }, []);

  // Update dancer options (like workshop level)
  const updateDancerOptions = useCallback(
    async (id: string, options: { workshopLevel: string }): Promise<void> => {
      // Find dancer to update
      const dancer = selectedDancers.find(
        (d) =>
          (d.participantId && d.participantId === id) ||
          (d.dancerId && d.dancerId === id) ||
          (d.originalDancerId && d.originalDancerId === id)
      );

      if (!dancer) {
        console.error("Could not find dancer with ID:", id);
        return;
      }

      console.log("Updating dancer:", dancer.firstName, dancer.lastName, "with options:", options);

      // Update local state only - no API calls yet
      setSelectedDancers((prev) =>
        prev.map((d) => {
          if (
            (d.participantId && d.participantId === id) ||
            (d.dancerId && d.dancerId === id) ||
            (d.originalDancerId && d.originalDancerId === id)
          ) {
            return {
              ...d,
              workshopLevel: options.workshopLevel,
              options: {
                ...(d.options || {}),
                workshopLevel: options.workshopLevel,
                competition: d.isFromRoutine ? true : d.options?.competition || false,
              },
              // Mark as pending if it's not already created
              pendingCreation: d.pendingCreation || !d.participantId
            };
          }
          return d;
        })
      );

      // Update registration data in local state
      const updatedDancers = selectedDancers.map(d => {
        if (
          (d.participantId && d.participantId === id) ||
          (d.dancerId && d.dancerId === id) ||
          (d.originalDancerId && d.originalDancerId === id)
        ) {
          return {
            ...d,
            workshopLevel: options.workshopLevel,
            options: {
              ...(d.options || {}),
              workshopLevel: options.workshopLevel,
              competition: d.isFromRoutine ? true : d.options?.competition || false,
            },
            pendingCreation: d.pendingCreation || !d.participantId
          };
        }
        return d;
      });

      updateRegistrationData({
        registrationDancers: updatedDancers,
        dancers: updatedDancers,
        dancerIds: updatedDancers.map(d => d.dancerId || d.originalId)
      });
      
      // No API calls here anymore - moved to handleNext
    },
    [selectedDancers, updateRegistrationData]
  );

  // Cancel deselection warning
  const cancelDeselect = useCallback(() => {
    setShowWarning(false);
    setWarningDancer(null);
  }, []);

  // Handle next button click
  const handleNext = useCallback((): Participant[] | undefined => {
    // Validation: Check for missing workshop levels - use improved function
    const missingLevel = selectedDancers.some(
      (dancer) => !getWorkshopLevel(dancer)
    );

    if (missingLevel) {
      alert(
        "Please select a workshop level for all dancers before proceeding."
      );
      return;
    }

    if (selectedDancers.length === 0) {
      setError("Please select at least one dancer to continue");
      return;
    }

    try {
      // Set loading state while we process the API calls
      setLoading(true);
      
      // Process pending creations and updates in the background
      // This is important: we return results immediately for UI flow
      // while API operations happen asynchronously
      
      // First create a copy of the dancers to return
      const dancersWithPricing = selectedDancers.map((dancer) => {
        // Get workshop level
        const workshopLevel = getWorkshopLevel(dancer);

        // Get price for that level
        const level = workshopLevels.find(
          (lvl) => lvl.id === workshopLevel || lvl.label === workshopLevel
        );

        return {
          ...dancer,
          calculatedPrice: level?.price || 0,
        };
      });

      // Extract dancer IDs
      const dancerIds = dancersWithPricing.map((d) => d.dancerId || d.originalId || '');

      // Update registration data
      updateRegistrationData({
        registrationDancers: dancersWithPricing,
        dancers: dancersWithPricing,
        dancerIds: dancerIds,
      });

      // Process all dancers asynchronously (don't wait for it)
      processAllDancers().catch(error => {
        console.error("Error processing dancers:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        setError(`Failed to process dancers: ${errorMessage}`);
      });

      // Return the dancers with pricing for the next step immediately
      return dancersWithPricing;
    } catch (error) {
      console.error("Error preparing dancers:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      setError(`Failed to prepare dancers: ${errorMessage}`);
      return undefined;
    }
  }, [selectedDancers, workshopLevels, setError, updateRegistrationData, getWorkshopLevel, registrationData]);

  // Helper function to process all dancers in the background
  const processAllDancers = async () => {
    if (!registrationData.addParticipant || !registrationData.updateParticipant || !registrationData.removeParticipant) {
      return;
    }
    
    try {
      // Make a copy of the dancers to process
      const processedDancers = [...selectedDancers];
      
      // Track all dancers that exist in the backend (have participantId)
      const existingParticipantIds = new Set();
      
      // First pass: create new dancers and track existing ones
      for (let i = 0; i < processedDancers.length; i++) {
        const dancer = processedDancers[i];
        
        // If dancer already exists in the backend, add to tracking set
        if (dancer.participantId) {
          existingParticipantIds.add(dancer.participantId);
        }
        
        // Skip dancers that are already in DynamoDB
        if (!dancer.pendingCreation) continue;
        
        // Skip dancers without workshop level
        const workshopLevel = getWorkshopLevel(dancer);
        if (!workshopLevel) continue;
        
        console.log(`Creating new dancer in DynamoDB: ${dancer.firstName} ${dancer.lastName}`);
        
        // Create dancer record
        const dancerData = {
          dancerId: dancer.dancerId,
          originalId: dancer.dancerId,
          type: "dancer",
          firstName: dancer.firstName,
          lastName: dancer.lastName,
          dateOfBirth: dancer.dateOfBirth || dancer.birthDate,
          workshopLevel: workshopLevel,
          options: {
            fullConvention: true,
            competition: dancer.isFromRoutine || false,
            workshopLevel: workshopLevel,
          },
        };
        
        try {
          // Call API to create the dancer
          const participantId = await registrationData.addParticipant(dancerData, "dancer");
          
          if (participantId) {
            // Update the dancer with the new participantId
            processedDancers[i] = {
              ...dancer,
              participantId,
              pendingCreation: false
            };
            
            // Add to tracking set
            existingParticipantIds.add(participantId);
            
            console.log(`Successfully created dancer: ${dancer.firstName} ${dancer.lastName} with ID: ${participantId}`);
          }
        } catch (error) {
          console.error(`Error creating dancer ${dancer.firstName} ${dancer.lastName}:`, error);
          // Continue processing other dancers
        }
      }
      
      // Second pass: update existing dancers that have workshop level changes
      for (const dancer of processedDancers) {
        // Skip dancers that don't have participantId
        if (!dancer.participantId) continue;
        
        // Skip routine dancers that don't need updates
        if (dancer.isFromRoutine && !dancer.workshopLevel) continue;
        
        console.log(`Updating dancer in DynamoDB: ${dancer.firstName} ${dancer.lastName}`);
        
        try {
          // Call API to update the dancer
          await registrationData.updateParticipant(dancer.participantId, {
            workshopLevel: dancer.workshopLevel,
            options: {
              ...(dancer.options || {}),
              workshopLevel: dancer.workshopLevel,
              competition: dancer.isFromRoutine ? true : dancer.options?.competition || false,
            },
          });
          
          console.log(`Successfully updated dancer: ${dancer.firstName} ${dancer.lastName}`);
        } catch (error) {
          console.error(`Error updating dancer ${dancer.firstName} ${dancer.lastName}:`, error);
          // Continue processing other dancers
        }
      }

      // Third pass: Check for dancers who were previously in the DB but are now deleted from local state
      // Collect all dancers that were originally from the backend
      const previousDancers = registrationData.dancers || [];
      
      // Find dancers with participantIds that are no longer in the selected dancers list
      for (const prevDancer of previousDancers) {
        // Skip if no participantId (wasn't in backend)
        if (!prevDancer.participantId) continue;
        
        // Skip if it's still in our current selection
        if (existingParticipantIds.has(prevDancer.participantId)) continue;
        
        // Skip if it's from a routine (these should not be removable)
        if (prevDancer.isFromRoutine || prevDancer.fromRoutine || prevDancer.routineSource) continue;
        
        console.log(`Dancer was removed in UI: ${prevDancer.firstName} ${prevDancer.lastName}, deleting from backend...`);
        
        try {
          // This dancer was in the backend but is no longer in selectedDancers - delete it
          await registrationData.removeParticipant(prevDancer.participantId);
          console.log(`Successfully deleted dancer: ${prevDancer.firstName} ${prevDancer.lastName}`);
        } catch (error) {
          console.error(`Error deleting dancer ${prevDancer.firstName} ${prevDancer.lastName}:`, error);
          // If there's an error deleting, might be because the dancer is part of a routine
          if (error instanceof Error && error.message.includes('routine')) {
            console.warn(`Dancer ${prevDancer.firstName} ${prevDancer.lastName} could not be deleted because they're part of a routine`);
          }
        }
      }
      
      // Update local state with processed dancers in case they have new participantIds
      setSelectedDancers(processedDancers);
    } finally {
      setLoading(false);
    }
  };

  // === EFFECTS ===

  // Load dancers from API
  useEffect(() => {
    const fetchDancers = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(ROSTER_API) as ApiResponse;

        // Filter to dancers with "Dancer" role
        const allMembers = response.roster || [];
        const dancersOnly = allMembers.filter((member: RosterMember) => {
          const roles = normalizeRoles(member.role);
          return roles.includes("Dancer");
        });

        // Convert RosterMembers to Participants
        const dancersAsParticipants = dancersOnly.map(rosterMemberToParticipant);
        
        setDancers(dancersAsParticipants);
        
        // Once dancers are loaded, we can process registration data
        processRegistrationData(dancersAsParticipants);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDancers();
  }, [normalizeRoles]);

  // Process registration data when dancers are loaded
  const processRegistrationData = useCallback((dancerRoster: Participant[]) => {
    // Only run this if we have data to process
    if (
      (!selectedDancers.length && (registrationData.dancers?.length || registrationData.dancersFromRoutines?.length || registrationData.routines?.length)) ||
      (registrationData.dancers?.length && selectedDancers.length !== registrationData.dancers.length)
    ) {
      // Create map to eliminate duplicates
      // IMPORTANT CHANGE: We now use a more robust key creation function that considers multiple IDs
      const dancerMap = new Map<string, Participant>();
      
      // Helper function to generate a consistent ID for deduplication
      const getDancerUniqueKey = (dancer: any): string => {
        // Try to use the most unique identifiers first, then fall back to less unique ones
        // Check for originalId first as it indicates a dancer that might appear elsewhere
        if (dancer.originalId) return dancer.originalId;
        
        // Next check for dancerId as it's a core identifier
        if (dancer.dancerId) return dancer.dancerId;
        
        // Then participantId which might be a registration-specific ID
        if (dancer.participantId) return dancer.participantId;
        
        // If all else fails, try to create a composite key from name + DOB
        // This is useful for catching dancers where the ID system has failed
        if (dancer.firstName && dancer.lastName && (dancer.dateOfBirth || dancer.birthDate)) {
          return `${dancer.firstName}-${dancer.lastName}-${dancer.dateOfBirth || dancer.birthDate}`;
        }
        
        // Last resort - use whatever ID-like field we have
        return dancer.id || dancer.participantId || dancer.dancerId || 'unknown';
      };
      
      console.log("Processing registration data with dancers:", dancerRoster.length);
      console.log("Routines:", registrationData.routines?.length || 0);
      console.log("DancersFromRoutines:", registrationData.dancersFromRoutines?.length || 0);

      // Process dancers from registration data first (these have most of the correct fields)
      if (Array.isArray(registrationData.dancers)) {
        registrationData.dancers.forEach((dancer) => {
          const key = getDancerUniqueKey(dancer);

          if (key) {
            dancerMap.set(key, {
              ...dancer,
              workshopLevel: dancer.workshopLevel || dancer.options?.workshopLevel,
              options: {
                ...(dancer.options || {}),
                workshopLevel: dancer.workshopLevel || dancer.options?.workshopLevel,
              },
            });

            // Mark dancers with workshop levels as user-selected
            if (dancer.workshopLevel || dancer.options?.workshopLevel) {
              setUserSelectedLevels((prev) => ({
                ...prev,
                [key]: true,
              }));
            }
          }
        });
      }

      // Process dancers from routines - but be careful not to overwrite complete data
      const dancersFromRoutines = registrationData.dancersFromRoutines || [];
      if (dancersFromRoutines.length > 0) {
        console.log("Processing dancersFromRoutines:", dancersFromRoutines);
        dancersFromRoutines.forEach((dancer) => {
          // Get unique key for this dancer
          const key = getDancerUniqueKey(dancer);

          if (key) {
            // Find dancer details in roster if needed
              const dancerDetails = dancerRoster.find(
              (d) => d.dancerId === dancer.dancerId || d.dancerId === dancer.originalId
              );
              
            // Check if dancer already exists in our map
            if (dancerMap.has(key)) {
              // Update existing entry with routine info without losing other data
              const existingDancer = dancerMap.get(key);

              if (existingDancer) {
              dancerMap.set(key, {
                  ...existingDancer,
                  id: existingDancer.id || dancer.id || key, // Ensure id is not undefined
                  // Preserve existing workshopLevel if present
                  workshopLevel: existingDancer.workshopLevel || dancer.workshopLevel || dancer.options?.workshopLevel,
                  // Preserve the existing originalId or add one if not present
                  originalId: existingDancer.originalId || dancer.dancerId,
                  // Mark as from routine if not already marked
                isFromRoutine: true,
                  fromRoutine: dancer.routineName || dancer.fromRoutine || existingDancer.fromRoutine || "routine",
                  // Preserve cannotBeRemoved status
                  cannotBeRemoved: existingDancer.cannotBeRemoved || true,
                  options: {
                    ...(existingDancer.options || {}),
                    // Preserve existing workshopLevel
                    workshopLevel: existingDancer.options?.workshopLevel || dancer.options?.workshopLevel,
                    // Ensure competition is set to true for routine dancers
                    competition: true,
                  },
                });
              }
            } else {
              // No existing entry, create new one
              dancerMap.set(key, {
                ...(dancerDetails || {
                  firstName: 'Unknown',
                  lastName: 'Dancer',
                  participantType: 'dancer',
                  id: key // Ensure id is present in the default object
                }),
                id: dancerDetails?.id || key, // Handle possible undefined dancerDetails
                dancerId: dancerDetails?.dancerId || dancer.dancerId, // Ensure dancerId is set
                isFromRoutine: true,
                fromRoutine: dancer.routineName || dancer.fromRoutine || "routine",
                routineName: dancer.routineName || dancer.fromRoutine || "routine",
                originalId: dancer.originalId || dancer.dancerId,
                cannotBeRemoved: true,
                // Try to get suggested workshop level if not already set
                workshopLevel: dancerDetails && dancerDetails.workshopLevel,
                participantType: 'dancer', // Ensure participantType is set
                options: {
                  fullConvention: true,
                  competition: true,
                  // Store suggested workshop level in options as well
                  workshopLevel: dancerDetails && dancerDetails.workshopLevel,
                },
              });
            }
          }
        });
      }

      // Process routines
      const routines = registrationData.routines || [];
      if (routines.length > 0) {
        console.log("Processing routines:", routines);
        routines.forEach((routine) => {
          const routineTitle = routine.title || routine.name || "Unknown Routine";

          // Extract dancer IDs
          let dancerIds: string[] = [];
          const dancersArray = routine.dancers || [];

          if (Array.isArray(dancersArray)) {
            // Handle possible DynamoDB format where each element is an object with an S property
            dancerIds = dancersArray
              .map((d) => {
                if (typeof d === "string") return d;
                if (d && typeof d === "object" && d.S) return d.S;
                return "";
              })
              .filter(Boolean);
          }
          
          console.log("Routine:", routineTitle, "Dancers:", dancerIds);

          // Process each dancer
          dancerIds.forEach((dancerId) => {
            // Find dancer in roster
            const dancerDetails = dancerRoster.find((d) => d.dancerId === dancerId);
            
            // If found in roster, check if this dancer already exists in our map
            if (dancerDetails) {
              // Try multiple potential keys for this dancer
              const key = getDancerUniqueKey(dancerDetails);
              
              if (dancerMap.has(key)) {
                // Update existing entry with routine info
                const existingDancer = dancerMap.get(key);
                
                if (existingDancer) {
                  dancerMap.set(key, {
                    ...existingDancer,
                    id: existingDancer.id || (dancerDetails ? dancerDetails.id : key), // Ensure id is not undefined
                    // Add routine info without overwriting existing data
                    isFromRoutine: true,
                    fromRoutine: existingDancer.fromRoutine || routineTitle,
                    routineName: existingDancer.routineName || routineTitle,
                    cannotBeRemoved: existingDancer.cannotBeRemoved || true,
                    originalId: existingDancer.originalId || dancerId,
                    // Preserve workshopLevel if already set, otherwise leave unset
                    workshopLevel: existingDancer.workshopLevel,
                    options: {
                      ...(existingDancer.options || {}),
                      fullConvention: true,
                      competition: true,
                      // Preserve workshopLevel if already set
                      workshopLevel: existingDancer.options?.workshopLevel,
                    },
                  });
                }
              } else {
                // No existing entry, create new one
                dancerMap.set(key, {
                  ...(dancerDetails || {
                    firstName: 'Unknown',
                    lastName: 'Dancer',
                    participantType: 'dancer',
                    id: key // Ensure id is present in the default object
                  }),
                  id: dancerDetails?.id || key, // Handle possible undefined dancerDetails
                  dancerId: dancerDetails?.dancerId || dancerId, // Ensure dancerId is set
                isFromRoutine: true,
                fromRoutine: routineTitle,
                routineName: routineTitle,
                  originalId: dancerId,
                cannotBeRemoved: true,
                  // Try to get suggested workshop level if not already set
                  workshopLevel: dancerDetails && dancerDetails.workshopLevel,
                  participantType: 'dancer', // Ensure participantType is set
                options: {
                  fullConvention: true,
                  competition: true,
                    // Store suggested workshop level in options as well
                    workshopLevel: dancerDetails && dancerDetails.workshopLevel,
                },
              });
              }
            } else {
              // If dancer not found in roster, still add a placeholder
              // Use dancerId as the key since we have nothing else
              dancerMap.set(dancerId, {
                id: dancerId,
                dancerId: dancerId,
                originalId: dancerId,
                firstName: "Unknown",
                lastName: "Dancer",
                isFromRoutine: true,
                fromRoutine: routineTitle,
                routineName: routineTitle,
                cannotBeRemoved: true,
                workshopLevel: null,
                participantType: 'dancer',
                options: {
                  fullConvention: true,
                  competition: true,
                  workshopLevel: null,
                },
              });
              console.log("Added placeholder for dancer:", dancerId);
            }
          });
        });
      }

      // Update selected dancers
      const processedDancers = Array.from(dancerMap.values());
      console.log("Processed dancers:", processedDancers.length);
      setSelectedDancers(processedDancers);
    }
  }, [registrationData, selectedDancers.length, setUserSelectedLevels]);

  return {
    // State
    loading,
    error,
    dancers,
    filteredDancers,
    searchQuery,
    setSearchQuery,
    filteredSelectedDancers,
    selectedSearchQuery,
    setSelectedSearchQuery,
    selectedDancers,
    showWarning,
    warningDancer,
    workshopLevels,
    totalPrice,
    ageDivisions,
    
    // Remove confirmation dialog state
    showRemoveConfirmation,
    dancerToRemove,
    confirmDancerRemoval,
    cancelDancerRemoval,
    
    // UI State handlers
    cancelDeselect,
    
    // Utility functions
    calculateAge,
    getInitials,
    formatAgeRange,
    getSuggestedWorkshopLevel,
    isDancerSelected,
    formatCurrency,
    findAgeGroupInfo,
    getWorkshopLevel,
    
    // Event handlers
    toggleDancerSelection,
    updateDancerOptions,
    handleNext
  };
};

export default useDancersData; 